import { useEffect, useState } from "react";
import { useValue } from "@tldraw/state-react";
import { ChevronRight, Locate, Redo2, Undo2 } from "lucide-react";
import type { TLComment, TLRecord, TLShapeId } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import { api } from "./api";
import { AgentMark, agentName, useAgentStyle } from "./AgentIdentity";
import { toolLabel } from "./AgentResources";
import {
  jumpToShape,
  reverseFromThread,
  threadConversation,
  useAgentThreads,
} from "./agent-threads";
import { agentReplyMeta } from "../shared/agent-comments";
import { recordLabel, type AgentDraft } from "../shared/agent-drafts";
import "./local-agents.css";

/*
 * Agent replies in a comment thread are drawn as the conversation's agent in
 * each viewer's chosen style, with the objects they changed and Undo/Redo.
 */

const agentOf = (comment: TLComment) => {
  const meta = agentReplyMeta(comment);
  return meta
    ? {
        id: meta.conversationId,
        agent: comment.meta.agentNumber as number | undefined,
      }
    : null;
};

/** The author's name: a member's own, or the agent's name in the viewer's style. */
export function CommentAuthor({
  comment,
  userId,
}: {
  comment: TLComment;
  userId: string;
}) {
  const style = useAgentStyle(userId);
  const agent = agentOf(comment);
  return (
    <>{agent ? agentName(style, agent) : String(comment.meta.author || "Collaborator")}</>
  );
}

export function CommentAuthorMark({
  comment,
  userId,
  size,
}: {
  comment: TLComment;
  userId: string;
  size: number;
}) {
  const style = useAgentStyle(userId);
  const agent = agentOf(comment);
  return agent ? (
    <AgentMark style={style} draft={agent} state="idle" size={size} />
  ) : null;
}

/** Jump buttons for what the turn changed, and Undo/Redo for the caller and any editor. */
export function AgentReplyActions({
  comment,
  editor,
  canEdit,
}: {
  comment: TLComment;
  editor: Editor;
  canEdit: boolean;
}) {
  const meta = agentReplyMeta(comment);
  const { enabled } = useAgentThreads();
  const [busy, setBusy] = useState(false);
  const shapes = useValue(
    "agent reply objects",
    () =>
      (meta?.changed ?? []).flatMap((id) => {
        const shape = editor.getShape(id as TLShapeId);
        return shape ? [{ id, label: recordLabel(shape as TLRecord) }] : [];
      }),
    [editor, meta?.changed.join()],
  );
  if (!meta) return null;
  return (
    <div className="agent-reply-actions">
      {shapes.map((s) => (
        <button
          key={s.id}
          type="button"
          className={meta.undone ? "undone" : undefined}
          onClick={() => jumpToShape(editor, s.id)}
          title="Show on the canvas"
        >
          <Locate size={12} aria-hidden="true" />
          {s.label.slice(0, 40)}
        </button>
      ))}
      {meta.batchId && canEdit && enabled && (
        <button
          type="button"
          className="agent-reply-undo"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await reverseFromThread(meta.conversationId, meta.batchId!, meta.undone);
            setBusy(false);
          }}
        >
          {meta.undone ? (
            <>
              <Redo2 size={12} aria-hidden="true" /> Redo
            </>
          ) : (
            <>
              <Undo2 size={12} aria-hidden="true" /> Undo
            </>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * "Milo is working…" under a thread while the viewer's own call runs, with
 * the live steps on demand. Only the caller sees it; the conversation is private.
 */
export function ThreadAgentStatus({
  threadId,
  userId,
}: {
  threadId: string;
  userId: string;
}) {
  const style = useAgentStyle(userId);
  const { enabled, endpoint, list } = useAgentThreads();
  const summary = enabled ? threadConversation(list, threadId) : undefined;
  const working = summary?.status === "working";
  const [draft, setDraft] = useState<AgentDraft | null>(null);
  useEffect(() => {
    if (!working || !summary) return;
    let cancelled = false;
    void api<AgentDraft>(`${endpoint}/${summary.id}`)
      .then((d) => !cancelled && setDraft(d))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [working, endpoint, summary?.id, summary?.version]);
  if (!working || !summary) return null;
  const turnStart =
    draft?.messages.filter((m) => m.role === "user").at(-1)?.at ?? Infinity;
  const calls = (draft?.toolCalls ?? []).filter((c) => c.at >= turnStart);
  const running = calls.find((c) => c.state === "running");
  return (
    <details className="thread-agent-status">
      <summary>
        <AgentMark style={style} draft={summary} state="working" size={18} />
        <span>
          {agentName(style, summary)} is working…
          <small>
            {running
              ? toolLabel(running.name, true)
              : calls.length
                ? "Thinking"
                : "Reading the comment"}
          </small>
        </span>
        {calls.length > 0 && <ChevronRight size={13} aria-hidden="true" />}
      </summary>
      {calls.length > 0 && (
        <ol className="agent-activity">
          {calls.map((c) => (
            <li key={c.id} data-state={c.state}>
              <span>{toolLabel(c.name, c.state === "running")}</span>
            </li>
          ))}
        </ol>
      )}
    </details>
  );
}
